import { useEffect, memo, useCallback } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Container,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Card,
} from '@mui/material';
import renderDynamicComponent from './components/RenderDynamicComponent';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { isArray } from 'lodash';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { CheckSharp, PlayArrowSharp } from '@mui/icons-material';
import {
  getActionProperties,
  getStep,
  getStepProperties,
  isChecklistItemValid,
  workflowInstanceData,
} from 'src/careflow/ph-core/workflow/helpers/workflow';
import IconRequired from 'src/assets/icon_required.svg';
import useObjectState from 'src/hooks/useObjectState';
import DispositionEndDialog from './components/common/dialog/DispositionEndDialog';
import { ArrowStepConnector, styleTabAll, useStyles } from 'src/careflow/helpers/workflowStyle';
import { mapDataAndDataWorkflowInstance } from './helpers/util';
import { WorkflowService } from './api/WorkflowService';
import StepCard from './components/common/ui/StepCard';
import { Header } from './components/common/ui/Header';
import { InstructionBox } from './components/common/ui/InstructionBox';
import { LabelTab } from './components/common/ui/LabelTab';
import { ErrorContainer } from './components/common/ui/ErrorContainer';
import { getWorkflowItemHandler } from './utils';
import { CommonHeader } from '../../../components/workflow/common/BasicComponents';
import {
  checkAclValidation,
  WORKFLOW_RESET_ALLOWED_ACLS,
} from '../admin/permissions/util/permission.utils';
import { getStep as getStepComponentRegistry } from 'src/careflow/ph-core/workflow/registry/workflowRegistry';

/**
 * @interface WorkflowDataState
 * @description Defines the shape of the state object that holds workflow-specific data.
 */
interface WorkflowDataState {
  /** The main workflow definition object. */
  workflow: any;
  /** Parsed metadata from the workflow definition. */
  workflowMetadata: any;
  /** An array of stages for the current workflow, sorted by order. */
  stages: any;
  /** A list of all possible transitions in the workflow. */
  transitionList: any;
  /** The checklist items for the currently active stage. */
  checklist: any;
  /** All checklist items for all stages of the workflow. */
  allChecklist: any;
}

interface CommonDataState {
  isLoading: boolean;
  showErrorContainer: boolean;
  errorContainerTitle: any;
  errorContainerList: any;
  showStageInstructions: boolean;
  showStepInstructions: boolean;
  selectedTab: string;
  defaultStage: any;
  activeStep: any;
  dataHandler: any;
  isTransitioning: boolean;
}

export interface WorkflowInstance {
  id?: string;
  stage?: {
    id?: string;
    order: number;
    code?: string;
    name?: string;
    description?: string;
  };
  status?: string;
  workFlow?: {
    id?: string;
    code?: string;
    name?: string;
    metadata?: string;
  };
  data?: string; // json string with scope.checklistItem
  closeOn?: string;
  referenceValue?: string;
  referenceName?: string;
  [key: string]: any;
}

export interface WorkflowData {
  id?: string | number;
  recordId?: string | null;
  recordReference?: string;
  fhirId?: string;
  workflowInstance?: WorkflowInstance;
  referralRecord?: {
    fhirId?: string;
    [key: string]: any;
  } | null;
  caseRecord?: {
    workflowOwnedBy?: string;
    [key: string]: any;
  } | null;
  [key: string]: any;
}

export interface WorkflowProps {
  data: WorkflowData;
  refreshRecord: () => void;
  initialWorkflow?: string;
  workflowHandler: any;
  workflowData?: any;
  i18Path?: string;
  namespace?: string;
  onTransitionSuccess?: (scenario: 'next' | 'last', transition?: any) => void;
  checklistRefreshKey?: any;
}

/**
 * @component Workflow
 * @description Renders a dynamic workflow based on a defined structure.
 * It manages stages, steps (checklist items), transitions, and user interactions
 * for a given record. It's highly configurable through props to handle different
 * workflow scenarios and custom UI components.
 *
 * @param {object} props - The component props.
 * @param {any} props.data - The main data object for the record associated with the workflow instance.
 * @param {() => void} props.refreshRecord - A function to call to refresh the record data from the parent.
 * @param {string} props.initialWorkflow - The code of the initial workflow to be loaded.
 * @param {object} props.componentMapping - A mapping of component names to React components for rendering dynamic checklist items.
 * @param {object} props.workflowHandler - An object containing handler functions for custom workflow actions.
 * @param {object} props.workflowData - Additional data for the workflow, like dialog states.
 * @param {string} props.i18Path - The base path for internationalization strings.
 * @param {string} props.namespace - The namespace for internationalization strings.
 *
 * @returns {JSX.Element} The rendered workflow component.
 */
const Workflow = ({
  data,
  refreshRecord,
  initialWorkflow,
  workflowHandler,
  workflowData,
  i18Path,
  namespace,
  onTransitionSuccess,
  checklistRefreshKey,
}: WorkflowProps) => {
  const { i18n } = useLocales();
  const user = useAuth();
  const classes = useStyles({});

  const [
    { workflow, workflowMetadata, stages, transitionList, checklist, allChecklist },
    updateWorkflowState,
  ] = useObjectState<WorkflowDataState>({
    workflow: null,
    workflowMetadata: null,
    stages: [],
    transitionList: [],
    checklist: [],
    allChecklist: null,
  });

  const [
    {
      isLoading,
      showErrorContainer,
      errorContainerTitle,
      errorContainerList,
      showStageInstructions,
      showStepInstructions,
      selectedTab,
      defaultStage,
      activeStep,
      dataHandler,
      isTransitioning,
    },
    updateCommonDataState,
  ] = useObjectState<CommonDataState>({
    isLoading: false,
    showErrorContainer: false,
    errorContainerTitle: null,
    errorContainerList: [],
    showStageInstructions: false,
    showStepInstructions: false,
    selectedTab: '',
    defaultStage: data?.workflowInstance?.stage?.order ?? null,
    activeStep: data?.workflowInstance?.stage?.order ?? null,
    dataHandler: null,
    isTransitioning: false,
  });

  const memoWorkflowInstance = data?.workflowInstance;

  /**
   * @function fetchWorkFlow
   * @description Fetches the workflow definition by its code, sorts its stages, and updates the component state.
   */
  const fetchWorkFlow = useCallback(async () => {
    try {
      const getWorkflow = await WorkflowService.getWorkflowByCode(initialWorkflow);
      const orderedStages =
        getWorkflow?.stages && isArray(getWorkflow?.stages)
          ? getWorkflow?.stages.sort((a: any, b: any) => a.order - b.order)
          : [];
      const workflowMetadata = getWorkflow?.metadata && JSON.parse(getWorkflow?.metadata);

      updateWorkflowState({
        workflow: getWorkflow,
        stages: orderedStages,
        workflowMetadata: workflowMetadata,
      });
    } catch (error) {
      console.log(error);
    }
  }, [initialWorkflow, updateWorkflowState]);

  /**
   * @function fetchTransitions
   * @description Fetches all available transitions for the current workflow.
   */
  const fetchTransitions = useCallback(async () => {
    const getTransitions = await WorkflowService.getTransitions(workflow?.id);
    updateWorkflowState({ transitionList: getTransitions });
  }, [workflow?.id, updateWorkflowState]);

  /**
   * @function getAllChecklistByStage
   * @description Fetches all checklist items for every stage in the workflow.
   * @returns {Promise<any[]>} A promise that resolves to a flat array of all checklist items.
   */
  const getAllChecklistByStage = useCallback(async () => {
    const results = await Promise.all(
      stages?.map(async (e: any) => {
        try {
          const res = await WorkflowService.getChecklistById({
            checklistId: e?.id,
            id: data?.id,
            referenceName: data?.recordReference,
            referenceValue: data?.recordId,
          });
          return res;
        } catch (error) {
          console.error(`Error fetching checklist`, error);
          return [];
        }
      }) || []
    );

    return results.flat();
  }, [stages, data?.id, data?.recordReference, data?.recordId]);

  /**
   * @function getActiveStageChecklist
   * @description Filters and sorts the checklist items for the currently active step.
   * @param {any} step - The current active step object.
   * @param {any[]} allChecklist - The complete list of checklist items for all stages.
   */
  const getActiveStageChecklist = useCallback(
    (step: any, allChecklist: any) => {
      try {
        if (step && allChecklist?.length) {
          if (!step) return;

          const getChecklist = allChecklist?.filter((e: any) => e?.checkListId === step.id);

          if (!getChecklist?.length) {
            updateWorkflowState({ checklist: [] });
            return;
          }

          const sortChecklistItems = getChecklist.sort((a: any, b: any) => {
            try {
              const parseConditionA = JSON.parse(a.conditions)?.order ?? 0;
              const parseConditionB = JSON.parse(b.conditions)?.order ?? 0;
              return parseConditionA - parseConditionB;
            } catch (error) {
              return 0;
            }
          });

          updateWorkflowState({ checklist: sortChecklistItems });
        } else {
          updateWorkflowState({ checklist: [] });
        }
      } catch (error) {
        console.error('Error fetching checklist:', error);
      }
    },
    [updateWorkflowState]
  );

  useEffect(() => {
    if (!initialWorkflow) return;
    fetchWorkFlow();
  }, [initialWorkflow, fetchWorkFlow]);

  useEffect(() => {
    fetchTransitions();
  }, [workflow, fetchTransitions]);

  useEffect(() => {
    const initiChecklist = async () => {
      try {
        updateCommonDataState({ isLoading: true });

        if (!memoWorkflowInstance?.stage && stages?.length === 0) return;

        const stage = memoWorkflowInstance?.stage;
        const step = { ...stage, order: stage?.order ? stage?.order - 1 : undefined };

        updateCommonDataState({
          activeStep: step,
          defaultStage: stage?.order,
        });

        const checklist = await getAllChecklistByStage();
        updateWorkflowState({ allChecklist: checklist });
        getActiveStageChecklist(step, checklist);
      } catch (error) {
        console.error('Error initializing checklist:', error);
      } finally {
        updateCommonDataState({
          isLoading: false,
          showStageInstructions: false,
          showStepInstructions: false,
          showErrorContainer: false,
          errorContainerTitle: null,
          errorContainerList: [],
        });
      }
    };

    if (memoWorkflowInstance?.stage && stages?.length > 0) initiChecklist();
  }, [
    memoWorkflowInstance,
    stages,
    getActiveStageChecklist,
    getAllChecklistByStage,
    updateCommonDataState,
    updateWorkflowState,
    memoWorkflowInstance?.stage,
  ]);

  useEffect(() => {
    if (!checklistRefreshKey || !activeStep) return;
    const refresh = async () => {
      const updated = await getAllChecklistByStage();
      updateWorkflowState({ allChecklist: updated });
      getActiveStageChecklist(activeStep, updated);
    };
    refresh();
  }, [checklistRefreshKey, activeStep]);

  useEffect(() => {
    if (!checklist || checklist.length === 0) return;

    const checklistValid = checklist.reduce((validCode, item) => {
      try {
        const isValid = isChecklistItemValid(item);
        return isValid ? item.code : validCode;
      } catch {
        return validCode;
      }
    }, null);

    const fallbackTab = checklistValid ?? checklist[0]?.code;
    updateCommonDataState((prev) => {
      const currentStillExists =
        prev.selectedTab && checklist.some((item: any) => item?.code === prev.selectedTab);
      return { selectedTab: currentStillExists ? prev.selectedTab : fallbackTab };
    });
  }, [checklist]);

  /**
   * @function attemptTransition
   * @description Attempts to perform a transition and checks for its validity.
   * @param {any} id - The ID of the transition to attempt.
   * @param {boolean} [notUpdateStage=false] - If true, the stage will not be updated after a successful transition attempt.
   * @param {string} [workflowInstanceData] - Optional data to be saved with the workflow instance.
   * @returns {Promise<boolean>} A promise that resolves to true if the transition is valid, otherwise false.
   */
  const attemptTransition = async (
    id: any,
    notUpdateStage: boolean = false,
    workflowInstanceData?: string
  ) => {
    const getTransitionId = transitionList?.find((i: any) => i.id === id);

    const { isValid, reasonNotValid } = await WorkflowService.attempt({
      //TODO: eliminate reference to case Record, and referralRecord that id specific to case workflows.
      fhirId: data?.fhirId || data?.referralRecord?.fhirId,
      ...(data?.caseRecord?.workflowOwnedBy && { ownedBy: data?.caseRecord?.workflowOwnedBy }),
      transition: { id: getTransitionId?.id },
      record: { id: data?.id },
      apply: true,
      originalData: data,
    });
    const parseResultValidation = typeof isValid === 'string' ? JSON.parse(isValid) : isValid;

    if (parseResultValidation) {
      if (!notUpdateStage) {
        await WorkflowService.updateStage({
          reference_name: data?.recordReference,
          reference_value: data?.recordId,
          record: { id: data?.id },
          stage: { id: getTransitionId?.toStagesId },
          workflow: { id: workflow?.id },
          transition: { id: getTransitionId?.id },
          ...(workflowInstanceData && { data: workflowInstanceData }),
        });
      }
      return true;
    } else {
      const { errorMessage = null } = JSON.parse(getTransitionId?.metadata)?.ui?.action ?? {};
      const message = errorMessage ? errorMessage : i18n('errorMessages.defaultWorkflow', 'crs');

      updateCommonDataState({
        showErrorContainer: true,
        errorContainerTitle: message,
        errorContainerList: [{ reasonNotValid }],
      });
      return false;
    }
  };

  /**
   * @function handleTransition
   * @description Handles the execution of a workflow transition. It can trigger custom actions,
   * update the record status, and navigate between stages.
   * @param {any} transition - The transition object to be handled.
   * @param {string} [workflowInstanceData] - Optional data to be saved with the workflow instance.
   */
  const handleTransition = async (transition: any, workflowInstanceData?: string) => {
    try {
      const { ui } = JSON.parse(transition?.metadata);

      if (ui?.action && Object.keys(ui?.action).length > 0) {
        const actionProperties: any = getActionProperties(workflowHandler, ui?.action);
        const { handler } = actionProperties;
        const { customTransition = false, openModal = false, dataAction = null } = ui?.action;

        const isValid = await attemptTransition(transition?.id, customTransition);
        if (!isValid) return;

        if (customTransition) {
          const dataHandler = {
            ...(dataAction && { ...dataAction }),
            transitionId: transition?.id,
          };

          await handler(openModal ? openModal : dataHandler);
          updateCommonDataState({ dataHandler: dataHandler });
          return;
        } else {
          await handlerAction(
            {
              status: ui?.action?.status,
              subStatus: ui?.action?.subStatus,
              referenceName: data?.recordReference,
              referenceValue: data?.recordId,
              transitionId: transition?.id,
              ...(workflowInstanceData && { data: workflowInstanceData }),
            },
            'next'
          );
          return;
        }
      }
      const isValid = await attemptTransition(transition?.id, false, workflowInstanceData);

      if (isValid) {
        refreshWorkFlow();
        onTransitionSuccess?.('next', transition);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  /**
   * @function handlerAction
   * @description A generic handler for actions that update the workflow instance with new status,
   * sub-status, and disposition details.
   * @param {any} dataAction - An object containing the data for the action.
   */
  const handlerAction = async (dataAction: any, scenario: 'next' | 'last' = 'last') => {
    try {
      const isValid = await attemptTransition(dataAction?.transitionId);

      const payload = {
        status: dataAction?.status,
        subStatus: dataAction?.subStatus,
        referenceName: data?.recordReference,
        referenceValue: data?.recordId,
        dispositionReason: dataAction?.reason ?? null,
        dispositionNote: dataAction?.reasonNote ?? null,
        dispositionOn: dataAction?.reason ? new Date().toISOString() : null,
        dispositionBy: dataAction?.reason ? user?.getCurrentUser()?.email : null,
        ...(dataAction?.data && { data: dataAction?.data }),
      };

      if (isValid) {
        await WorkflowService.updateWorkflowInstance(payload);
        const transition = transitionList?.find((t: any) => t.id === dataAction?.transitionId);
        onTransitionSuccess?.(scenario, transition);
      }
    } catch {
    } finally {
      refreshWorkFlow();
    }
  };

  /**
   * @function goToNextStep
   * @description Logic to navigate to the next step in the workflow if certain conditions are met.
   * @param {boolean} stageCompleted - Whether the current stage is completed.
   * @param {number} indexStep - The index of the next step.
   * @param {any} stepNext - The code of the next step.
   */
  const goToNextStep = async (stageCompleted: boolean, indexStep: number, stepNext: any) => {
    const notDefaultStage = indexStep > defaultStage;
    const transitionStage = transitionList?.find(
      (item: any) =>
        item.fromStagesId === memoWorkflowInstance?.stage?.id && item.toStagesId === stepNext?.id
    );

    if (notDefaultStage && !stageCompleted && transitionStage) {
      const { data } = mapDataAndDataWorkflowInstance(
        memoWorkflowInstance,
        user?.getCurrentUser()?.email,
        stepNext?.code,
        memoWorkflowInstance?.stage?.code || ''
      );
      await handleTransition(transitionStage, data);
    }
  };

  /**
   * @function goToLastStep
   * @description Handles the logic when the user navigates to the last step of the workflow.
   * This often involves presenting a dialog for final disposition.
   * @param {any} lastStep - The last step object of the workflow.
   */
  const goToLastStep = async (lastStep: any) => {
    if (!lastStep) return;

    if (memoWorkflowInstance?.status === 'Close') {
      goToBackOrDefaultStage(lastStep.code);
      return;
    }

    const previousStage = getStep(steps, lastStep?.code, 'previous');
    const dialogFromStageId = activeStep?.id === lastStep?.id ? previousStage?.id : activeStep?.id;

    const { transitionsWithoutAction, transitionsWithAction } = transitionList?.reduce(
      (acc: any, item: any) => {
        const meta = item?.metadata ? JSON.parse(item.metadata) : null;
        const { type = null } = meta?.ui?.action ?? {};
        const hasAction = Boolean(type);
        const fromLastStep = item.fromStagesId === lastStep?.id;
        const fromActiveStep = item.fromStagesId === dialogFromStageId;
        const toLastStep = item.toStagesId === lastStep?.id;

        if (hasAction && fromLastStep) {
          acc.transitionsWithAction.push(item);
          return acc;
        }

        const isValidWithoutAction = fromActiveStep && toLastStep ? true : false;

        if (!hasAction && isValidWithoutAction) {
          acc.transitionsWithoutAction.push(item);
        }

        return acc;
      },
      {
        transitionsWithoutAction: [],
        transitionsWithAction: [],
      }
    ) ?? { transitionsWithoutAction: [], transitionsWithAction: [] };

    const validateTransitionsAction = await Promise.all(
      transitionsWithoutAction?.map(async (item: any) => {
        let isValid = await attemptTransition(item.id, true);
        updateCommonDataState({
          showErrorContainer: false,
          errorContainerTitle: null,
          errorContainerList: [],
        });
        return {
          ...item,
          isValid,
        };
      })
    );

    const meta = transitionsWithAction?.[0]?.metadata
      ? JSON.parse(transitionsWithAction?.[0]?.metadata)
      : null;

    const ui = meta?.ui;
    const action = ui?.action;

    if (!action || Object.keys(action).length === 0) return;

    const { handler } = getActionProperties(workflowHandler, action);
    const { openModal = false, dataAction = null } = action;

    const { data } = mapDataAndDataWorkflowInstance(
      memoWorkflowInstance,
      user?.getCurrentUser()?.email,
      lastStep.code,
      memoWorkflowInstance?.stage?.code || ''
    );

    const dataClose = mapDataAndDataWorkflowInstance(
      {
        ...memoWorkflowInstance,
        ...(data && { data: data }),
      },
      user?.getCurrentUser()?.email,
      lastStep.code,
      lastStep.code
    );

    const dataHandler = {
      ...(dataAction && { ...dataAction }),
      transitions: validateTransitionsAction,
      workflowInstanceData: dataClose?.data,
    };

    updateCommonDataState({ dataHandler });
    await handler(openModal);
  };

  /**
   * @function goToBackOrDefaultStage
   * @description Navigates the view to a specific stage by its code, updating the active step and fetching its checklist.
   * @param {string} code - The code of the stage to navigate to.
   */
  const goToBackOrDefaultStage = async (code) => {
    const stageByCode = stages?.find((s) => s?.code === code);
    const step = { ...stageByCode, order: stageByCode.order - 1 };
    updateCommonDataState({
      activeStep: step,
      selectedTab: '',
    });
    getActiveStageChecklist(step, allChecklist);
  };

  const steps = isArray(stages)
    ? stages.map((i: any) => ({ name: i?.name, id: i?.id, code: i?.code, order: i?.order }))
    : [];

  const lastStepExternal = steps?.find((i) => i?.order === steps?.length) ?? null;

  const openDispositionDialog = () => {
    if (memoWorkflowInstance?.status === 'Close') return;
    goToLastStep(lastStepExternal);
  };

  const canResetWorkflow = checkAclValidation({
    acls: WORKFLOW_RESET_ALLOWED_ACLS,
  });

  const handleResetWorkflow = async () => {
    if (!data?.recordReference || !data?.recordId) return;
    try {
      await WorkflowService.resetWorkflow({
        referenceName: data.recordReference,
        referenceValue: data.recordId,
      });
      refreshWorkFlow();
    } catch (err) {
      console.error('Reset workflow failed', err);
    }
  };

  const refreshChecklistOnly = async () => {
    updateCommonDataState({
      showErrorContainer: false,
      errorContainerTitle: null,
      errorContainerList: [],
    });
    const checklist = await getAllChecklistByStage();
    updateWorkflowState({ allChecklist: checklist });
    if (activeStep?.id) {
      getActiveStageChecklist(activeStep, checklist);
    }
  };

  const handleClearStep = async (checklistItemCode: string) => {
    if (!data?.recordReference || !data?.recordId || !checklistItemCode) return;
    try {
      await WorkflowService.clearStep({
        referenceName: data.recordReference,
        referenceValue: data.recordId,
        checklistItemCode,
      });
      refreshChecklistOnly();
    } catch (err) {
      console.error('Clear step failed', err);
    }
  };

  const handlerStep = async (step: any, options: { viaClose?: boolean } = {}) => {
    const { viaClose = false } = options;

    const { indexStep, isLastStep, stageCompleted, isDefault } = getStepProperties({
      steps,
      step,
      memoWorkflowInstance,
      defaultStage,
    });

    const stepStatus = stageCompleted
      ? stageCompleted
      : isDefault || indexStep < defaultStage
      ? stepsCompleted(step?.id)
      : false;

    updateCommonDataState({
      showErrorContainer: false,
      errorContainerTitle: null,
      errorContainerList: [],
    });

    if (viaClose) {
      const backStage = activeStep?.order + 1 < defaultStage;
      const dataStep = backStage ? (stepStatus ? step : null) : step;
      return goToLastStep(dataStep);
    }

    if (isLastStep) {
      const movingForward = activeStep?.id !== step?.id && !stepStatus;
      const nextTransition = transitionList?.find(
        (item: any) =>
          item.fromStagesId === memoWorkflowInstance?.stage?.id && item.code.includes('NEXT')
      );

      if (movingForward && nextTransition) {
        const isValid = await attemptTransition(nextTransition.id, true);
        if (!isValid) return;
      }

      return goToBackOrDefaultStage(step?.code);
    }

    if (!stepStatus && !isDefault) {
      if (closeWorkflow) return;
      return goToNextStep(stepStatus, indexStep, step);
    }

    return goToBackOrDefaultStage(step?.code);
  };

  /**
   * @function refreshWorkFlow
   * @description Resets error states and re-fetches the main record data to refresh the workflow state.
   */
  const refreshWorkFlow = () => {
    updateCommonDataState({
      showErrorContainer: false,
      errorContainerTitle: null,
      errorContainerList: [],
    });
    updateWorkflowState({ checklist: [] });
    refreshRecord();
  };

  /**
   * @function stepsCompleted
   * @description Determines if a step (stage) is completed by checking the validity of its required checklist items.
   * @param {string} stepId - The ID of the step/stage to check.
   * @returns {boolean} True if the step is considered complete, otherwise false.
   */
  const stepsCompleted = (stepId: string): boolean => {
    const stageItems = allChecklist?.filter((item) => item?.checkListId === stepId) ?? [];
    const requiredItems = stageItems.filter((item) => item?.isRequired);

    const itemListValid = stageItems.length > 0 && stageItems.every(isChecklistItemValid);

    const allRequiredValid = requiredItems.every(isChecklistItemValid);

    return itemListValid || requiredItems.length === 0 || allRequiredValid;
  };

  const isWorkflowCompleted = memoWorkflowInstance?.status === 'Close';
  const lastStage = stages?.length > 0 ? stages[stages.length - 1] : null;

  const isOnTrueLastStage = activeStep && lastStage && activeStep.order + 1 === lastStage.order;

  const { closeWorkflow = false } = workflowInstanceData(memoWorkflowInstance, 'rules') ?? {};

  useEffect(() => {
    if (!workflowData?.openDispositionExternal || !lastStepExternal) return;
    goToLastStep(lastStepExternal);
  }, [workflowData?.openDispositionExternal]);

  return (
    <Container disableGutters maxWidth="xl" data-name="workflow-Container">
      <Card
        sx={{ backgroundColor: '#f4f6f8', boxShadow: 'none', borderRadius: 0 }}
        data-name="workflow-stepper-card"
      >
        <Stepper
          activeStep={activeStep?.order}
          connector={<ArrowStepConnector />}
          sx={{
            mb: 1,
            justifyContent: 'center',
            '& .MuiStep-root': {
              minWidth: 0,
              display: 'flex',
              justifyContent: 'center',
            },
          }}
        >
          {steps?.map((step: any, index: number) => (
            <Step key={step?.id}>
              <StepLabel
                classes={{
                  active: classes.active,
                  completed: classes.completed,
                }}
                StepIconComponent={(props) => {
                  const { indexStep, isLastStep, stageCompleted, isDefault } = getStepProperties({
                    steps,
                    step,
                    memoWorkflowInstance,
                    defaultStage,
                  });
                  const stepStatus = stageCompleted
                    ? stageCompleted
                    : isDefault || indexStep < defaultStage
                    ? stepsCompleted(step?.id)
                    : false;
                  const disabled = isDefault ? false : !stepStatus && !props?.active;

                  return (
                    <StepCard
                      active={props?.active}
                      handlerGoToStep={() =>
                        isLastStep ? openDispositionDialog() : handlerStep(step)
                      }
                      stepStatus={stepStatus}
                      step={step}
                      allChecklist={allChecklist}
                      indexStep={indexStep}
                      disabled={isLastStep ? false : disabled}
                      isLastStep={isLastStep}
                      i18n={i18n}
                    />
                  );
                }}
              />
            </Step>
          ))}
        </Stepper>
      </Card>
      <Card>
        {!isLoading && activeStep && (
          <CommonHeader
            itemIsValid={stepsCompleted(activeStep?.id)}
            name={activeStep?.name ? `${activeStep?.order + 1}. ${activeStep?.name}` : ''}
            handler={() => updateCommonDataState({ showStageInstructions: true })}
            sx={{ mx: 2, mt: 2, mb: 1, width: 'calc(100% - 32px)' }}
            actions={
              isWorkflowCompleted ? (
                <>
                  <Button
                    variant="contained"
                    disabled
                    startIcon={<CheckSharp />}
                    sx={{
                      backgroundColor: '#00AB55 !important',
                      color: '#fff !important',
                      opacity: 0.8,
                    }}
                  >
                    Workflow Completed
                  </Button>
                  {canResetWorkflow && (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={handleResetWorkflow}
                      data-name="workflow-reset-button"
                      sx={{ marginLeft: 'auto !important' }}
                    >
                      Workflow Reset
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button
                    className={classes.closeBtn}
                    onClick={() => {
                      const lastItem = getStep(steps, activeStep?.code, 'last');
                      handlerStep(lastItem, { viaClose: true });
                    }}
                    disabled={
                      isTransitioning || (closeWorkflow && activeStep?.order + 1 !== defaultStage)
                    }
                    variant="contained"
                    size="small"
                  >
                    Close
                  </Button>
                  {!isOnTrueLastStage && (
                    <Button
                      className={classes.nextBtn}
                      onClick={() => {
                        const nextStep = getStep(steps, activeStep?.code, 'next');
                        handlerStep(nextStep);
                      }}
                      disabled={isTransitioning || closeWorkflow}
                      variant="contained"
                      size="small"
                    >
                      Next
                    </Button>
                  )}
                  {canResetWorkflow && (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={handleResetWorkflow}
                      data-name="workflow-reset-button"
                      sx={{ marginLeft: 'auto !important' }}
                    >
                      Workflow Reset
                    </Button>
                  )}
                </>
              )
            }
          />
        )}

        {showStageInstructions && (
          <InstructionBox
            title={i18n('case.details.workflow.instruction.stageTitle', namespace)}
            description={activeStep?.description}
            handler={() => updateCommonDataState({ showStageInstructions: false })}
          />
        )}

        <Box display="grid" gridTemplateColumns="3fr 9fr">
          <Card sx={{ background: 'white', height: '100%', borderRadius: 0 }}>
            <Box
              display="flex"
              flexWrap="wrap"
              alignContent="flex-start"
              justifyContent="space-evenly"
              sx={{ width: '100%' }}
            >
              {!isLoading ? (
                <Tabs
                  data-name="workflow-checklists"
                  orientation="vertical"
                  value={selectedTab}
                  variant="standard"
                  sx={{ width: '100%', mx: 2, mt: 1, mb: 2 }}
                  onChange={(_, newSelectedTab) => {
                    updateCommonDataState({
                      selectedTab: newSelectedTab,
                      showStepInstructions: false,
                    });
                  }}
                  TabIndicatorProps={{
                    sx: { display: 'none' },
                  }}
                >
                  {checklist &&
                    isArray(checklist) &&
                    checklist.map((item: any, index: number) => {
                      const metadata = item?.metadata && JSON.parse(item.metadata);
                      const itemIsValid = isChecklistItemValid(item);
                      const typeComponentTab = metadata?.ui?.component?.typeComponentTab;
                      const ComponentToRender =
                        typeComponentTab && getStepComponentRegistry(typeComponentTab);
                      const isRequired = item?.isRequired ?? false;

                      const isDispositionItem =
                        metadata?.ui?.component?.type === 'Disposition' &&
                        memoWorkflowInstance?.status !== 'Close';
                      const handleDispositionClick = isDispositionItem
                        ? openDispositionDialog
                        : undefined;

                      const tabComponent = ComponentToRender ? (
                        <ComponentToRender
                          data-name="workflow-checklist-item-component"
                          key={item?.id}
                          value={item?.code}
                          component="button"
                          sx={styleTabAll}
                          itemName={item?.name}
                          isValid={itemIsValid}
                          checklistItem={item}
                          isRequired={isRequired}
                          {...(handleDispositionClick && { onClick: handleDispositionClick })}
                        />
                      ) : (
                        <Tab
                          data-name="workflow-checklist-item-tab"
                          key={item?.id}
                          value={item?.code}
                          component="button"
                          sx={styleTabAll}
                          {...(handleDispositionClick && { onClick: handleDispositionClick })}
                          icon={
                            itemIsValid ? (
                              <CheckSharp />
                            ) : isRequired ? (
                              <IconRequired />
                            ) : (
                              <PlayArrowSharp />
                            )
                          }
                          iconPosition="start"
                          label={<LabelTab name={item?.name} isRequired={isRequired} />}
                        />
                      );
                      return tabComponent;
                    })}
                </Tabs>
              ) : (
                <CircularProgress />
              )}
            </Box>
            <ErrorContainer
              showErrorContainer={showErrorContainer}
              errorContainerTitle={errorContainerTitle}
              errorContainerList={errorContainerList}
            />
          </Card>

          <Card
            sx={{ background: 'white', height: '100%', borderRadius: 0 }}
            data-name="workflow-action-area"
          >
            {!isLoading ? (
              <Box display="grid">
                {checklist &&
                  isArray(checklist) &&
                  checklist.map((item: any, index: number) => {
                    const isMatched = item?.code === selectedTab;
                    const itemIsValid = isChecklistItemValid(item);
                    const metadata = item?.metadata && JSON.parse(item.metadata);
                    const { data = null, component = null } = metadata?.ui ?? {};
                    const confirmButton = data?.confirm;
                    const { editable = true } = component ?? {};
                    const activeStepOrder = activeStep?.order + 1;
                    const inDefaultStep = Boolean(activeStepOrder === defaultStage);
                    const allowEdit = inDefaultStep || editable;

                    const handlerConfirm = () =>
                      workflowHandler?.CONFIRM?.handler({
                        data,
                        memoWorkflowInstance,
                        checklistItem: item,
                      });

                    return (
                      isMatched && (
                        <Box key={`component-${index}`} data-name="workflow-component-box">
                          <Grid container display={'flex'} sx={{ mt: 2, mb: 2, px: 2 }}>
                            <Grid item xs={8}>
                              <Header
                                itemIsValid={itemIsValid}
                                name={item?.name ?? ''}
                                handler={() =>
                                  updateCommonDataState({ showStepInstructions: true })
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              display={'flex'}
                              justifyContent={'flex-end'}
                              alignItems={'center'}
                              gap={1}
                            >
                              {confirmButton && allowEdit && (
                                <Button
                                  variant="contained"
                                  onClick={handlerConfirm}
                                >{`Confirm ${item?.name}`}</Button>
                              )}
                              {canResetWorkflow && (
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  onClick={() => handleClearStep(item?.code)}
                                  data-name={`workflow-clear-step-${item?.code}`}
                                  sx={{ ml: 'auto' }}
                                >
                                  Clear Step
                                </Button>
                              )}
                            </Grid>
                          </Grid>
                          {showStepInstructions && (
                            <InstructionBox
                              title={i18n('case.details.workflow.instruction.stepTitle', namespace)}
                              description={item?.description}
                              handler={() => updateCommonDataState({ showStepInstructions: false })}
                            />
                          )}
                          {renderDynamicComponent(
                            memoWorkflowInstance,
                            item,
                            {
                              ...(getWorkflowItemHandler(workflowHandler, item) || {}),
                              allowEdit,
                            },
                            i18n,
                            i18Path || '',
                            namespace || ''
                          )}
                        </Box>
                      )
                    );
                  })}
              </Box>
            ) : (
              <Box flex={1} display={'flex'} justifyContent={'center'} alignItems={'center'} mt={2}>
                <CircularProgress />
              </Box>
            )}
          </Card>
        </Box>
      </Card>
      <DispositionEndDialog
        isOpen={workflowData?.openDialog}
        handlerAction={handlerAction}
        dataHandler={dataHandler}
        handlerIsDialog={(value) => {
          workflowHandler?.setOpenDialog?.handler(value);
        }}
        i18n={i18n}
      />
    </Container>
  );
};

export default memo(Workflow);
